import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ScoreBadge, daysUntil, decodeEntities } from "../lib/format";

const COLUMN_DEFS = [
  ["planning", "Planning / PIN"],
  ["tender", "Open / Tender"],
  ["award", "Awarded"],
];

function bucket(stage) {
  const s = (stage || "").toLowerCase();
  if (s.includes("planning") || s.includes("pin")) return "planning";
  if (s.includes("award")) return "award";
  return "tender";
}

export default function Pipeline() {
  const [columns, setColumns] = useState({ planning: [], tender: [], award: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Single, focused fetch — only what the stage board renders. The previous
    // implementation also pulled 400 MOD-pipeline rows in parallel which the
    // page never displayed once we removed the tab; cutting it shaves real
    // wall-clock time off every visit.
    let cancel = false;
    api
      .get("/notices", { params: { limit: 150, sort: "deadline_asc" } })
      .then((r) => {
        if (cancel) return;
        const cols = { planning: [], tender: [], award: [] };
        (r.data?.items || []).forEach((n) => {
          cols[bucket(n.stage)].push(n);
        });
        setColumns(cols);
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  return (
    <div data-testid="pipeline-page">
      <div className="mb-6">
        <div className="label-meta">Workspace</div>
        <div className="display">Pipeline</div>
        <p className="text-[13px] mt-1" style={{ color: "var(--parapet-text-secondary)" }}>
          Live notices from Find a Tender &amp; Contracts Finder, grouped by stage.
        </p>
      </div>

      {loading ? (
        <div className="p-8 text-center"><span className="spinner" /></div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {COLUMN_DEFS.map(([k, label]) => (
            <div
              key={k}
              className="p-card"
              style={{ padding: 12, background: "var(--parapet-surface)" }}
              data-testid={`pipeline-col-${k}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="label-meta">{label}</span>
                <span className="value-mono text-[11px]" style={{ color: "var(--parapet-text-secondary)" }}>
                  {columns[k].length}
                </span>
              </div>
              <div className="space-y-2 max-h-[72vh] overflow-y-auto">
                {columns[k].length === 0 ? (
                  <div className="text-[11px] p-3" style={{ color: "var(--parapet-text-secondary)" }}>Empty</div>
                ) : (
                  columns[k].slice(0, 40).map((n) => (
                    <Link
                      to={`/app/opportunities/${n.id}`}
                      key={n.id}
                      className="block p-card-tight hover:bg-white transition"
                      data-testid={`pipeline-card-${n.id}`}
                      style={{ background: "#fff" }}
                    >
                      <div className="flex items-start gap-2">
                        <ScoreBadge
                          score={n.fit?.score}
                          band={n.fit?.band}
                          insufficient={n.fit?.insufficient_data}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium truncate">
                            {decodeEntities(n.title)}
                          </div>
                          <div className="text-[11px] truncate" style={{ color: "var(--parapet-text-secondary)" }}>
                            {decodeEntities(n.buyer_name) || "Buyer not published"}
                          </div>
                          <div className="mt-1 flex items-center gap-1">
                            <span className="source-tag" style={{ pointerEvents: "none" }}>
                              {(n.source || "").replace(/_/g, " ")}
                            </span>
                            {n.deadline && (
                              <span className="text-[10px] value-mono" style={{ color: "var(--parapet-text-secondary)" }}>
                                {daysUntil(n.deadline)}d
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
